import type { UseFormReturn } from 'react-hook-form';
import { cn } from '@/lib/cn';
import { DateInput, Input, SegmentedControl, Select, Toggle } from '@/components/ui';
import type { SelectOption } from '@/components/ui';
import { NATIONALITY_OPTIONS } from '@/lib/nationalityOptions';
import { MaritalStatus, UserGender, type UserFieldAccess } from '@/types/user.types';
import type { EditUserFormValues } from '../../schemas/editUser.schemas';
import { FIELD_CONTROL, PropertyField, PropertyGroup, PropertyRow } from './PropertyList';

type Form = UseFormReturn<EditUserFormValues>;

const TODAY_ISO = new Date().toISOString().slice(0, 10);

const genderOptions: SelectOption[] = [
  { value: UserGender.MALE, label: 'Male' },
  { value: UserGender.FEMALE, label: 'Female' },
  { value: UserGender.OTHER, label: 'Other' },
];

const maritalOptions: SelectOption[] = [
  { value: MaritalStatus.SINGLE, label: 'Single' },
  { value: MaritalStatus.MARRIED, label: 'Married' },
  { value: MaritalStatus.DIVORCED, label: 'Divorced' },
  { value: MaritalStatus.WIDOWED, label: 'Widowed' },
  { value: MaritalStatus.SEPARATED, label: 'Separated' },
  { value: MaritalStatus.DOMESTIC_PARTNERSHIP, label: 'Domestic partnership' },
  { value: MaritalStatus.OTHER, label: 'Other' },
];

const employmentTypeOptions: SelectOption[] = [
  { value: 'full-time', label: 'Full-time' },
  { value: 'part-time', label: 'Part-time' },
  { value: 'contract', label: 'Contract' },
  { value: 'intern', label: 'Intern' },
];

const WORK_LOCATION_OPTIONS = [
  { value: 'onsite', label: 'On-site' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'remote', label: 'Remote' },
] as const;

const PAY_TYPE_OPTIONS = [
  { value: 'salaried', label: 'Salaried' },
  { value: 'hourly', label: 'Hourly' },
] as const;

function control(error?: string): string {
  return cn(FIELD_CONTROL, error && 'border-danger focus:border-danger/70 focus:ring-danger/10');
}

function TextField({
  form,
  name,
  label,
  type = 'text',
  hint,
  placeholder,
  error,
  inputMode,
}: {
  form: Form;
  name: Parameters<Form['register']>[0];
  label: string;
  type?: string;
  hint?: string;
  placeholder?: string;
  error?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
}): React.ReactElement {
  const id = `profile-${name.replace(/\./g, '-')}`;
  return (
    <PropertyField label={label} htmlFor={id} hint={hint} error={error}>
      <Input
        id={id}
        type={type}
        inputMode={inputMode}
        placeholder={placeholder}
        aria-invalid={!!error}
        className={control(error)}
        {...form.register(name)}
      />
    </PropertyField>
  );
}

export function OverviewEdit({
  form,
  access,
}: {
  form: Form;
  /** Hidden fields render read-only as "Hidden" and are never sent back. */
  access: UserFieldAccess;
}): React.ReactElement {
  const { errors } = form.formState;
  const gender = form.watch('personalInfo.gender') ?? '';
  const maritalStatus = form.watch('personalInfo.maritalStatus') ?? '';
  const nationality = form.watch('personalInfo.nationality') ?? '';

  return (
    <div className="space-y-7">
      <PropertyGroup title="Name & sign-in">
        <TextField
          form={form}
          name="firstName"
          label="First name"
          error={errors.firstName?.message}
        />
        <TextField form={form} name="lastName" label="Last name" error={errors.lastName?.message} />
        <TextField
          form={form}
          name="email"
          label="Work email"
          type="email"
          hint="Used to sign in. Changing it changes their login."
          error={errors.email?.message}
        />
        <TextField
          form={form}
          name="avatar"
          label="Photo link"
          type="url"
          placeholder="https://…"
          hint="Paste an image link; the preview updates above."
          error={errors.avatar?.message}
        />
      </PropertyGroup>

      <PropertyGroup title="Personal">
        {access.sensitive ? (
          <PropertyField
            label="Date of birth"
            htmlFor="profile-dob"
            error={errors.personalInfo?.dateOfBirth?.message}
          >
            <DateInput
              id="profile-dob"
              max={TODAY_ISO}
              className={control(errors.personalInfo?.dateOfBirth?.message)}
              {...form.register('personalInfo.dateOfBirth')}
            />
          </PropertyField>
        ) : (
          <PropertyRow label="Date of birth" value={null} hidden />
        )}
        <PropertyField label="Gender" htmlFor="profile-gender">
          <Select
            id="profile-gender"
            options={genderOptions}
            value={gender}
            placeholder="Not specified"
            triggerClassName={FIELD_CONTROL}
            onChange={(value) =>
              form.setValue(
                'personalInfo.gender',
                value as EditUserFormValues['personalInfo']['gender'],
                { shouldDirty: true }
              )
            }
          />
        </PropertyField>
        <PropertyField
          label="Nationality"
          htmlFor="profile-nationality"
          error={errors.personalInfo?.nationality?.message}
        >
          <Select
            id="profile-nationality"
            options={NATIONALITY_OPTIONS}
            value={nationality}
            placeholder="Choose a country"
            searchable
            searchPlaceholder="Search countries…"
            triggerClassName={FIELD_CONTROL}
            onChange={(value) =>
              form.setValue('personalInfo.nationality', value, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
          />
        </PropertyField>
        <PropertyField label="Marital status" htmlFor="profile-marital">
          <Select
            id="profile-marital"
            options={maritalOptions}
            value={maritalStatus}
            placeholder="Not specified"
            triggerClassName={FIELD_CONTROL}
            onChange={(value) =>
              form.setValue(
                'personalInfo.maritalStatus',
                value as EditUserFormValues['personalInfo']['maritalStatus'],
                { shouldDirty: true }
              )
            }
          />
        </PropertyField>
        {access.sensitive ? (
          <>
            <TextField
              form={form}
              name="personalInfo.cnicNumber"
              label="CNIC number"
              placeholder="00000-0000000-0"
              error={errors.personalInfo?.cnicNumber?.message}
            />
            <TextField
              form={form}
              name="personalInfo.passportNumber"
              label="Passport number"
              error={errors.personalInfo?.passportNumber?.message}
            />
          </>
        ) : (
          <>
            <PropertyRow label="CNIC number" value={null} hidden />
            <PropertyRow label="Passport number" value={null} hidden />
          </>
        )}
      </PropertyGroup>
    </div>
  );
}

export function EmploymentEdit({ form }: { form: Form }): React.ReactElement {
  const { errors } = form.formState;
  const employmentType = form.watch('employment.employmentType') ?? '';
  const workLocation = form.watch('employment.workLocation') || 'onsite';

  return (
    <div className="space-y-7">
      <PropertyGroup title="Position">
        <TextField
          form={form}
          name="employment.department"
          label="Department"
          error={errors.employment?.department?.message}
        />
        <TextField
          form={form}
          name="employment.designation"
          label="Designation"
          placeholder="e.g. Driver, Dispatcher"
          error={errors.employment?.designation?.message}
        />
        <PropertyField label="Employment type" htmlFor="profile-employment-type">
          <Select
            id="profile-employment-type"
            options={employmentTypeOptions}
            value={employmentType}
            placeholder="Not specified"
            triggerClassName={FIELD_CONTROL}
            onChange={(value) =>
              form.setValue(
                'employment.employmentType',
                value as EditUserFormValues['employment']['employmentType'],
                { shouldDirty: true }
              )
            }
          />
        </PropertyField>
        <PropertyField label="Work location">
          <SegmentedControl
            ariaLabel="Work location"
            size="sm"
            options={[...WORK_LOCATION_OPTIONS]}
            value={workLocation}
            onChange={(value) =>
              form.setValue(
                'employment.workLocation',
                value as EditUserFormValues['employment']['workLocation'],
                { shouldDirty: true }
              )
            }
          />
        </PropertyField>
      </PropertyGroup>

      <PropertyGroup
        title="Dates"
        description="Probation, confirmation and exit dates are set by the status actions below."
      >
        <PropertyField
          label="Joining date"
          htmlFor="profile-joining"
          error={errors.employment?.joiningDate?.message}
        >
          <DateInput
            id="profile-joining"
            className={control(errors.employment?.joiningDate?.message)}
            {...form.register('employment.joiningDate')}
          />
        </PropertyField>
      </PropertyGroup>
    </div>
  );
}

export function PayEdit({
  form,
  orgCurrency,
  access,
}: {
  form: Form;
  orgCurrency: string;
  /** Hidden fields render read-only as "Hidden" and are never sent back. */
  access: UserFieldAccess;
}): React.ReactElement {
  const { errors } = form.formState;
  const payType = form.watch('compensation.compensationType') || 'salaried';
  const currency = form.watch('compensation.currency') || orgCurrency;
  const taxExempt = Boolean(form.watch('compensation.taxInfo.taxExempt'));
  const isHourly = payType === 'hourly';
  const amountError = isHourly
    ? errors.compensation?.hourlyRate?.message
    : errors.compensation?.baseSalary?.message;

  return (
    <div className="space-y-7">
      {!access.payRates ? (
        <PropertyGroup title="Rate">
          <PropertyRow label="Pay rate" value={null} hidden />
        </PropertyGroup>
      ) : (
        <PropertyGroup title="Rate">
          <PropertyField label="Paid">
            <SegmentedControl
              ariaLabel="Compensation type"
              size="sm"
              options={[...PAY_TYPE_OPTIONS]}
              value={payType}
              onChange={(value) =>
                form.setValue(
                  'compensation.compensationType',
                  value as EditUserFormValues['compensation']['compensationType'],
                  { shouldDirty: true }
                )
              }
            />
          </PropertyField>
          <PropertyField
            label={isHourly ? 'Hourly rate' : 'Monthly salary'}
            htmlFor="profile-amount"
            error={amountError}
            hint="Leave blank to keep the current amount."
          >
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-500">
                {currency}
              </span>
              <Input
                key={payType}
                id="profile-amount"
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                className={cn(control(amountError), 'pl-12 tabular-nums')}
                {...form.register(isHourly ? 'compensation.hourlyRate' : 'compensation.baseSalary')}
              />
            </div>
          </PropertyField>
          <TextField
            form={form}
            name="compensation.currency"
            label="Currency"
            placeholder={orgCurrency}
            hint={`Defaults to the organisation currency (${orgCurrency}).`}
            error={errors.compensation?.currency?.message}
          />
        </PropertyGroup>
      )}

      {!access.sensitive ? (
        <PropertyGroup title="Bank & tax">
          <PropertyRow label="Bank details" value={null} hidden />
          <PropertyRow label="Tax details" value={null} hidden />
        </PropertyGroup>
      ) : (
        <>
          <PropertyGroup title="Bank">
            <TextField
              form={form}
              name="compensation.bankDetails.bankName"
              label="Bank"
              error={errors.compensation?.bankDetails?.bankName?.message}
            />
            <TextField
              form={form}
              name="compensation.bankDetails.accountNumber"
              label="Account number"
              inputMode="numeric"
              error={errors.compensation?.bankDetails?.accountNumber?.message}
            />
          </PropertyGroup>

          <PropertyGroup title="Tax">
            <TextField
              form={form}
              name="compensation.taxInfo.taxNumber"
              label="Tax number"
              error={errors.compensation?.taxInfo?.taxNumber?.message}
            />
            <TextField
              form={form}
              name="compensation.taxInfo.taxBracket"
              label="Tax bracket"
              error={errors.compensation?.taxInfo?.taxBracket?.message}
            />
            <PropertyField label="Tax exempt" htmlFor="profile-tax-exempt">
              <div className="flex h-9 items-center">
                <Toggle
                  id="profile-tax-exempt"
                  checked={taxExempt}
                  onChange={(checked) =>
                    form.setValue('compensation.taxInfo.taxExempt', checked, { shouldDirty: true })
                  }
                />
              </div>
            </PropertyField>
          </PropertyGroup>
        </>
      )}
    </div>
  );
}

export function ContactEdit({ form }: { form: Form }): React.ReactElement {
  const { errors } = form.formState;
  const contact = errors.contactInfo;
  const emergency = errors.personalInfo?.emergencyContact;

  return (
    <div className="space-y-7">
      <PropertyGroup title="Reach">
        <TextField
          form={form}
          name="contactInfo.phone"
          label="Phone"
          type="tel"
          error={contact?.phone?.message}
        />
        <TextField
          form={form}
          name="contactInfo.alternatePhone"
          label="Alternate phone"
          type="tel"
          error={contact?.alternatePhone?.message}
        />
        <TextField
          form={form}
          name="contactInfo.personalEmail"
          label="Personal email"
          type="email"
          error={contact?.personalEmail?.message}
        />
      </PropertyGroup>

      <PropertyGroup title="Address">
        <TextField
          form={form}
          name="contactInfo.address.street"
          label="Street"
          error={contact?.address?.street?.message}
        />
        <TextField
          form={form}
          name="contactInfo.address.city"
          label="City"
          error={contact?.address?.city?.message}
        />
        <TextField
          form={form}
          name="contactInfo.address.state"
          label="State / province"
          error={contact?.address?.state?.message}
        />
        <TextField
          form={form}
          name="contactInfo.address.postalCode"
          label="Postal code"
          error={contact?.address?.postalCode?.message}
        />
        <TextField
          form={form}
          name="contactInfo.address.country"
          label="Country"
          error={contact?.address?.country?.message}
        />
      </PropertyGroup>

      <PropertyGroup title="Emergency contact">
        <TextField
          form={form}
          name="personalInfo.emergencyContact.name"
          label="Name"
          error={emergency?.name?.message}
        />
        <TextField
          form={form}
          name="personalInfo.emergencyContact.relationship"
          label="Relationship"
          placeholder="e.g. Brother, Spouse"
          error={emergency?.relationship?.message}
        />
        <TextField
          form={form}
          name="personalInfo.emergencyContact.phone"
          label="Phone"
          type="tel"
          error={emergency?.phone?.message}
        />
        <TextField
          form={form}
          name="personalInfo.emergencyContact.email"
          label="Email"
          type="email"
          error={emergency?.email?.message}
        />
      </PropertyGroup>
    </div>
  );
}
